import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useTestMasters } from '../../hooks/useTestMasters';

const HealthGoalsModal = ({ isOpen, onClose, onSave, goal, mode }) => {
  const { allTestMasters } = useTestMasters();
  
  // Get unique biomarkers with their IDs from all test masters
  const BIOMARKERS = React.useMemo(() => {
    if (!allTestMasters || allTestMasters.length === 0) return [];
    const biomarkerMap = new Map();
    allTestMasters.forEach(test => {
      if (test.biomarker_name && !biomarkerMap.has(test.biomarker_name)) {
        biomarkerMap.set(test.biomarker_name, {
          name: test.biomarker_name,
          id: test._id || test.id || test.biomarker_id
        });
      }
    });
    return Array.from(biomarkerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allTestMasters]);
  
  const [formData, setFormData] = useState({
    goal_name: '',
    biomarkers: []
  });

  const [biomarkerSearch, setBiomarkerSearch] = useState('');
  const [showBiomarkerDropdown, setShowBiomarkerDropdown] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && goal) {
      // Convert biomarker IDs back to biomarker objects
      const biomarkerObjects = (goal.biomarkers || []).map(biomarkerId => {
        // Find the biomarker in BIOMARKERS array
        const biomarker = BIOMARKERS.find(b => b.id === biomarkerId);
        return biomarker || { id: biomarkerId, name: `Biomarker ${biomarkerId}` };
      });
      
      setFormData({
        goal_name: goal.goal_name || '',
        biomarkers: biomarkerObjects
      });
    } else {
      setFormData({
        goal_name: '',
        biomarkers: []
      });
    }
    setBiomarkerSearch('');
  }, [mode, goal, isOpen, BIOMARKERS]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddBiomarker = (biomarker) => {
    const biomarkerId = biomarker.id;
    const alreadyExists = formData.biomarkers.some(b => b.id === biomarkerId);
    
    if (!alreadyExists) {
      setFormData(prev => ({
        ...prev,
        biomarkers: [...prev.biomarkers, biomarker]
      }));
    }
    setBiomarkerSearch('');
    setShowBiomarkerDropdown(false);
  };

  const handleRemoveBiomarker = (biomarkerId) => {
    setFormData(prev => ({
      ...prev,
      biomarkers: prev.biomarkers.filter(b => b.id !== biomarkerId)
    }));
  };

  const filteredBiomarkers = BIOMARKERS.filter(b =>
    b.name.toLowerCase().includes(biomarkerSearch.toLowerCase()) &&
    !formData.biomarkers.some(existing => existing.id === b.id)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.goal_name.trim()) {
      alert('Please enter a goal name');
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">
            {mode === 'create' ? 'Add New Health Goal' : 'Edit Health Goal'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Goal Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Goal Name *
            </label>
            <input
              type="text"
              name="goal_name"
              value={formData.goal_name}
              onChange={handleInputChange}
              placeholder="e.g., Weight Loss, Muscle Gain, Better Sleep"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Biomarkers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Associated Biomarkers
            </label>
            <div className="relative mb-3">
              <input
                type="text"
                value={biomarkerSearch}
                onChange={(e) => setBiomarkerSearch(e.target.value)}
                onFocus={() => setShowBiomarkerDropdown(true)}
                onBlur={() => setTimeout(() => setShowBiomarkerDropdown(false), 200)}
                placeholder="Search and add biomarkers..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {showBiomarkerDropdown && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto z-10">
                  {filteredBiomarkers.length > 0 ? (
                    filteredBiomarkers.map(biomarker => (
                      <button
                        key={biomarker.id}
                        type="button"
                        onClick={() => handleAddBiomarker(biomarker)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors"
                      >
                        {biomarker.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No biomarkers found</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.biomarkers.map((biomarker) => (
                <div
                  key={biomarker.id}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {biomarker.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveBiomarker(biomarker.id)}
                    className="text-blue-600 hover:text-blue-800 font-bold"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              {mode === 'create' ? 'Create Goal' : 'Update Goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HealthGoalsModal;
